import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Ban, Landmark, ReceiptText, Tag, ArrowDownCircle, ArrowUpCircle, Download, FileText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const CURRENCIES = ["EUR", "USD", "TRY", "GBP"];
const eur = (n) => `€${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

async function download(url, filename) {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = href; a.download = filename; a.click();
    URL.revokeObjectURL(href);
  } catch { toast.error("Export failed"); }
}

export default function Finance() {
  const { user } = useAuth();
  const t = useT();
  const canEdit = ["owner", "operations_manager", "general_accountant"].includes(user?.role);
  return (
    <div className="p-6 sm:p-8 max-w-[1200px]">
      <PageHeader title={t("finance.title")} subtitle={t("finance.subtitle")} />
      <Tabs defaultValue="expenses">
        <TabsList data-testid="finance-tabs">
          <TabsTrigger value="expenses" data-testid="finance-tab-expenses">{t("finance.expenses")}</TabsTrigger>
          <TabsTrigger value="banks" data-testid="finance-tab-banks">{t("finance.banks")}</TabsTrigger>
          <TabsTrigger value="categories" data-testid="finance-tab-categories">{t("finance.categories")}</TabsTrigger>
          <TabsTrigger value="report" data-testid="finance-tab-report">{t("finance.report")}</TabsTrigger>
        </TabsList>
        <TabsContent value="expenses"><Expenses canEdit={canEdit} /></TabsContent>
        <TabsContent value="banks"><Banks canEdit={canEdit} /></TabsContent>
        <TabsContent value="categories"><Categories canEdit={canEdit} /></TabsContent>
        <TabsContent value="report"><ExpenseReport /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------- Expenses ----------------
function Expenses({ canEdit }) {
  const t = useT();
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [accts, setAccts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ category_id: "", amount: "", currency: "EUR", date: today(), vendor: "", notes: "", payment_source: "bank", bank_account_id: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/finance/expenses"),
      api.get("/finance/expense-categories?active=true"),
      api.get("/finance/bank-accounts"),
    ]).then(([e, c, a]) => { setRows(e.data); setCats(c.data); setAccts(a.data); })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.category_id || !form.amount) { toast.error(t("finance.catAmountReq")); return; }
    if (form.payment_source === "bank" && !form.bank_account_id) { toast.error(t("finance.selectBank")); return; }
    setBusy(true);
    try {
      const r = await api.post("/finance/expenses", { ...form, amount: Number(form.amount) });
      const file = document.getElementById("expense-receipt-file")?.files?.[0];
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/finance/expenses/${r.data.id}/receipt`, fd);
      }
      toast.success(t("finance.expenseRecorded"));
      setForm({ category_id: "", amount: "", currency: "EUR", date: today(), vendor: "", notes: "", payment_source: "bank", bank_account_id: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cancel = async (id) => {
    const reason = window.prompt(t("finance.cancelReason"));
    if (!reason) return;
    try { await api.post(`/finance/expenses/${id}/cancel`, { reason }); toast.success(t("finance.expenseCancelled")); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <>
      {canEdit && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-5" data-testid="expense-form">
          <h3 className="font-heading text-lg text-slate-900 mb-3 flex items-center gap-2"><Plus className="h-5 w-5" /> {t("finance.recordExpense")}</h3>
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t("finance.category")}>
              <Select value={form.category_id} onValueChange={(v) => setForm((f) => ({ ...f, category_id: v }))}>
                <SelectTrigger className="w-52 h-9" data-testid="expense-category"><SelectValue placeholder={t("finance.category")} /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select></Field>
            <Field label={t("finance.amount")}><Input type="number" step="0.01" value={form.amount} className="h-9 w-28" data-testid="expense-amount" onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} /></Field>
            <Field label={t("finance.currency")}>
              <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                <SelectTrigger className="h-9 w-20" data-testid="expense-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></Field>
            <Field label={t("finance.date")}><Input type="date" value={form.date} className="h-9 w-40" data-testid="expense-date" onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></Field>
            <Field label={t("finance.paidFrom")}>
              <Select value={form.payment_source} onValueChange={(v) => setForm((f) => ({ ...f, payment_source: v }))}>
                <SelectTrigger className="h-9 w-32" data-testid="expense-source"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="bank">{t("finance.bank")}</SelectItem><SelectItem value="cash">{t("finance.cashDrawer")}</SelectItem></SelectContent>
              </Select></Field>
            {form.payment_source === "bank" && (
              <Field label={t("finance.bankAccount")}>
                <Select value={form.bank_account_id} onValueChange={(v) => setForm((f) => ({ ...f, bank_account_id: v }))}>
                  <SelectTrigger className="w-48 h-9" data-testid="expense-bank"><SelectValue placeholder={t("finance.bankAccount")} /></SelectTrigger>
                  <SelectContent>{accts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select></Field>
            )}
            <Field label={t("finance.vendor")}><Input value={form.vendor} className="h-9 w-40" data-testid="expense-vendor" onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))} /></Field>
            <Field label={t("finance.receiptOptional")}><Input id="expense-receipt-file" type="file" className="h-9 w-52 text-xs" data-testid="expense-receipt" /></Field>
            <Button className="bg-navy hover:bg-navy-soft text-white h-9" disabled={busy} onClick={submit} data-testid="expense-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("finance.add")}</Button>
          </div>
        </div>
      )}
      {loading ? <Spin /> : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="expenses-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              <th className="px-4 py-2 font-medium">{t("finance.date")}</th><th className="px-4 py-2 font-medium">{t("finance.category")}</th>
              <th className="px-4 py-2 font-medium">{t("finance.vendor")}</th><th className="px-4 py-2 font-medium">{t("finance.amount")}</th>
              <th className="px-4 py-2 font-medium">EUR</th><th className="px-4 py-2 font-medium">{t("finance.paidFromCol")}</th>
              <th className="px-4 py-2 font-medium">{t("finance.status")}</th><th></th></tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">{t("finance.noExpenses")}</td></tr>}
              {rows.map((e) => (
                <tr key={e.id} className="border-t border-slate-50" data-testid={`expense-row-${e.id}`}>
                  <td className="px-4 py-2 text-slate-500">{(e.date || "").slice(0, 10)}</td>
                  <td className="px-4 py-2">{e.category_name} <Badge className={`ml-1 font-normal ${e.group === "travel" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>{e.group}</Badge></td>
                  <td className="px-4 py-2 text-slate-500">{e.vendor || "—"}</td>
                  <td className="px-4 py-2 tabular-nums">{e.amount} {e.currency}</td>
                  <td className="px-4 py-2 tabular-nums font-medium">{eur(e.eur_amount)}</td>
                  <td className="px-4 py-2 text-slate-500 capitalize">{e.payment_source}{e.bank_account_name ? ` · ${e.bank_account_name}` : ""}</td>
                  <td className="px-4 py-2"><Badge className={`font-normal capitalize ${e.status === "cancelled" ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-700"}`}>{e.status}</Badge>{e.receipt && <ReceiptText className="inline h-3.5 w-3.5 ml-1 text-slate-400" />}</td>
                  <td className="px-4 py-2">{canEdit && e.status !== "cancelled" && (
                    <Button size="sm" variant="ghost" className="h-7 text-red-600" data-testid={`expense-cancel-${e.id}`} onClick={() => cancel(e.id)}><Ban className="h-4 w-4" /></Button>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ---------------- Bank accounts ----------------
function Banks({ canEdit }) {
  const t = useT();
  const [accts, setAccts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", currency: "EUR", opening_balance: "" });
  const [txn, setTxn] = useState({ account_id: "", type: "deposit", amount: "", description: "" });
  const load = useCallback(() => {
    setLoading(true);
    api.get("/finance/bank-accounts").then((r) => setAccts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addAcct = async () => {
    if (!form.name) { toast.error(t("finance.accountNameReq")); return; }
    try { await api.post("/finance/bank-accounts", { ...form, opening_balance: Number(form.opening_balance || 0) }); toast.success(t("finance.accountCreated")); setForm({ name: "", currency: "EUR", opening_balance: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const addTxn = async () => {
    if (!txn.account_id || !txn.amount) { toast.error(t("finance.accountAmountReq")); return; }
    try { await api.post(`/finance/bank-accounts/${txn.account_id}/transactions`, { type: txn.type, amount: Number(txn.amount), description: txn.description }); toast.success(t("finance.txnPosted")); setTxn({ account_id: "", type: "deposit", amount: "", description: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <>
      {canEdit && (
        <div className="grid md:grid-cols-2 gap-4 mb-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="bank-form">
            <h3 className="font-heading text-slate-900 mb-3 flex items-center gap-2"><Landmark className="h-5 w-5" /> {t("finance.newBankAccount")}</h3>
            <div className="flex flex-wrap items-end gap-2">
              <Field label={t("finance.accountName")}><Input value={form.name} className="h-9 w-40" data-testid="bank-name" onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></Field>
              <Field label={t("finance.currency")}>
                <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                  <SelectTrigger className="h-9 w-20" data-testid="bank-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></Field>
              <Field label={t("finance.opening")}><Input type="number" step="0.01" value={form.opening_balance} className="h-9 w-28" data-testid="bank-opening" onChange={(e) => setForm((f) => ({ ...f, opening_balance: e.target.value }))} /></Field>
              <Button className="bg-navy text-white h-9" onClick={addAcct} data-testid="bank-add">{t("finance.create")}</Button>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="txn-form">
            <h3 className="font-heading text-slate-900 mb-3">{t("finance.manualTxn")}</h3>
            <div className="flex flex-wrap items-end gap-2">
              <Field label={t("finance.account")}>
                <Select value={txn.account_id} onValueChange={(v) => setTxn((t2) => ({ ...t2, account_id: v }))}>
                  <SelectTrigger className="h-9 w-36" data-testid="txn-account"><SelectValue placeholder={t("finance.account")} /></SelectTrigger>
                  <SelectContent>{accts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select></Field>
              <Field label={t("common.type")}>
                <Select value={txn.type} onValueChange={(v) => setTxn((t2) => ({ ...t2, type: v }))}>
                  <SelectTrigger className="h-9 w-28" data-testid="txn-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="deposit">{t("finance.deposit")}</SelectItem><SelectItem value="withdrawal">{t("finance.withdrawal")}</SelectItem></SelectContent>
                </Select></Field>
              <Field label={t("finance.amount")}><Input type="number" step="0.01" value={txn.amount} className="h-9 w-24" data-testid="txn-amount" onChange={(e) => setTxn((t2) => ({ ...t2, amount: e.target.value }))} /></Field>
              <Button className="bg-navy text-white h-9" onClick={addTxn} data-testid="txn-add">{t("finance.post")}</Button>
            </div>
          </div>
        </div>
      )}
      {loading ? <Spin /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="bank-list">
          {accts.length === 0 && <p className="text-slate-400 text-sm">{t("finance.noBankAccounts")}</p>}
          {accts.map((a) => (
            <div key={a.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`bank-card-${a.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-heading text-slate-900">{a.name}</span>
                <Badge className="bg-slate-100 text-slate-600 font-normal">{a.currency}</Badge>
              </div>
              <p className="text-2xl font-heading text-navy mt-2 tabular-nums">{eur(a.balance_eur)}</p>
              <p className="text-xs text-slate-400">{t("finance.openingShort")} {a.opening_balance} {a.currency}</p>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ---------------- Categories ----------------
function Categories({ canEdit }) {
  const t = useT();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", group: "operating" });
  const load = useCallback(() => {
    setLoading(true);
    api.get("/finance/expense-categories").then((r) => setCats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name) { toast.error(t("finance.nameReq")); return; }
    try { await api.post("/finance/expense-categories", form); toast.success(t("finance.categoryAdded")); setForm({ name: "", group: "operating" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggle = async (c) => {
    try { await api.patch(`/finance/expense-categories/${c.id}`, { active: !c.active }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <>
      {canEdit && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-5" data-testid="category-form">
          <div className="flex flex-wrap items-end gap-2">
            <Field label={t("finance.newCategory")}><Input value={form.name} className="h-9 w-56" data-testid="cat-name" onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></Field>
            <Field label={t("finance.dashboardGroup")}>
              <Select value={form.group} onValueChange={(v) => setForm((f) => ({ ...f, group: v }))}>
                <SelectTrigger className="h-9 w-36" data-testid="cat-group"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="operating">{t("finance.operating")}</SelectItem><SelectItem value="travel">{t("finance.travel")}</SelectItem></SelectContent>
              </Select></Field>
            <Button className="bg-navy text-white h-9" onClick={add} data-testid="cat-add"><Tag className="h-4 w-4 mr-1" /> {t("finance.add")}</Button>
          </div>
        </div>
      )}
      {loading ? <Spin /> : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="category-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="px-4 py-2 font-medium">{t("finance.category")}</th><th className="px-4 py-2 font-medium">{t("finance.group")}</th><th className="px-4 py-2 font-medium">{t("finance.status")}</th><th></th></tr></thead>
            <tbody>
              {cats.map((c) => (
                <tr key={c.id} className="border-t border-slate-50" data-testid={`cat-row-${c.id}`}>
                  <td className="px-4 py-2 font-medium text-slate-800">{c.name}</td>
                  <td className="px-4 py-2"><Badge className={`font-normal ${c.group === "travel" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>{c.group}</Badge></td>
                  <td className="px-4 py-2"><Badge className={`font-normal ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.active ? t("finance.active") : t("finance.inactive")}</Badge></td>
                  <td className="px-4 py-2">{canEdit && <Button size="sm" variant="ghost" className="h-7" data-testid={`cat-toggle-${c.id}`} onClick={() => toggle(c)}>{c.active ? t("finance.deactivate") : t("finance.activate")}</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ---------------- Expense report ----------------
function ExpenseReport() {
  const t = useT();
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/finance/expenses/report?start=${start}&end=${end}`).then((r) => setRep(r.data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail))).finally(() => setLoading(false));
  }, [start, end]);
  useEffect(() => { load(); }, [load]);
  const qs = `start=${start}&end=${end}`;

  return (
    <>
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <Field label={t("finance.date") + " " + t("common.from")}><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-40" data-testid="report-start" /></Field>
        <Field label={t("finance.date") + " " + t("common.to")}><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-40" data-testid="report-end" /></Field>
        <Button variant="outline" className="h-9" onClick={load} data-testid="report-apply">{t("common.apply")}</Button>
        <div className="flex gap-2 ml-auto">
          <Button variant="outline" className="h-9" data-testid="report-export-csv" onClick={() => download(`/finance/expenses/report/export?${qs}&format=csv`, `expenses_${start}_${end}.csv`)}><Download className="h-4 w-4 mr-1" /> CSV</Button>
          <Button variant="outline" className="h-9" data-testid="report-export-pdf" onClick={() => download(`/finance/expenses/report/export?${qs}&format=pdf`, `expenses_${start}_${end}.pdf`)}><FileText className="h-4 w-4 mr-1" /> PDF</Button>
        </div>
      </div>
      {loading || !rep ? <Spin /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6" data-testid="report-summary">
            <SumCard label={t("finance.travel")} value={eur(rep.by_group.travel)} tone="text-indigo-600" />
            <SumCard label={t("finance.operating")} value={eur(rep.by_group.operating)} />
            <SumCard label={t("finance.totalExpenses")} value={eur(rep.total_eur)} tone="text-navy" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="report-by-category">
              <h3 className="font-heading text-slate-900 mb-3">{t("finance.byCategory")}</h3>
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(rep.by_category).sort().map(([cat, v]) => (
                    <tr key={cat} className="border-t border-slate-50">
                      <td className="py-1.5">{cat} <Badge className={`ml-1 font-normal ${v.group === "travel" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>{v.group}</Badge></td>
                      <td className="py-1.5 text-right text-slate-400 text-xs">×{v.count}</td>
                      <td className="py-1.5 text-right tabular-nums font-medium">{eur(v.eur)}</td>
                    </tr>
                  ))}
                  {Object.keys(rep.by_category).length === 0 && <tr><td className="py-6 text-center text-slate-400">No expenses in range</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="report-by-bank">
              <h3 className="font-heading text-slate-900 mb-3">{t("finance.bySource")}</h3>
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(rep.by_source).sort().map(([src, v]) => (
                    <tr key={src} className="border-t border-slate-50">
                      <td className="py-1.5 flex items-center gap-1.5"><Landmark className="h-3.5 w-3.5 text-slate-400" />{src}</td>
                      <td className="py-1.5 text-right tabular-nums font-medium">{eur(v)}</td>
                    </tr>
                  ))}
                  {Object.keys(rep.by_source).length === 0 && <tr><td className="py-6 text-center text-slate-400">No expenses in range</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function SumCard({ label, value, tone = "text-slate-900" }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-xl font-heading ${tone}`}>{value}</p>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-xs text-slate-500 block mb-1">{label}</label>{children}</div>;
}
function Spin() {
  return <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>;
}
